import { useEffect, useMemo } from "react";
import styled from "styled-components";
import Alert from "@mui/lab/Alert";
import TextInput from "../../components/controls/text-input";
import Button from "../../components/controls/button";
import LoadingSpinner from "../../components/controls/loading-spinner";
import { ICON_ID } from "../../components/icons/google-icon";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { openDrawer } from "../../store/features/drawer/drawer-slice";
import { setCurrentPage } from "../../store/features/authentication/user-slice";
import { DrawerID } from "../../constants/drawer-constants";
import { fetchWhatsappSetting } from "../../store/features/organisation/whatsapp-slice";
import {
  downloadConversationMedia,
  fetchConversations,
  fetchMessages,
  resetCommunications,
  sendReply,
  setReplyText,
  setSearchText,
  setSelectedConversationId,
} from "../../store/features/organisation/communications-slice";
import { showError, showSuccess } from "../../components/notification/toastr-actions";
import { ApplicationError } from "../../models/errors/application-error";
import {
  WhatsappConversation,
  WhatsappMessage,
} from "../../services/organisation/whatsapp-communications-service";

type ConversationCardProps = {
  $selected: boolean;
};

type DeliveryTickState = "pending" | "sent" | "delivered" | "read" | "failed";

function formatDateTime(value?: string | null) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat([], {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function isOutgoing(direction?: string | null) {
  return !!direction && /out|sent|reply/i.test(direction);
}

function getLatestMessageStatus(message: WhatsappMessage) {
  const latestStatus = [...(message.statusHistory ?? [])].sort((left, right) => {
    const leftTime = new Date(left.timestamp ?? 0).getTime();
    const rightTime = new Date(right.timestamp ?? 0).getTime();
    return rightTime - leftTime;
  })[0]?.status;

  return message.currentStatus ?? latestStatus ?? "";
}

function getMessageFailureError(message: WhatsappMessage) {
  if (message.errorMessage) {
    return message.errorMessage;
  }

  const latestFailure = [...(message.statusHistory ?? [])]
    .sort((left, right) => {
      const leftTime = new Date(left.timestamp ?? 0).getTime();
      const rightTime = new Date(right.timestamp ?? 0).getTime();
      return rightTime - leftTime;
    })
    .find((status) => {
      const statusText = (status.status ?? "").toLowerCase();
      return statusText.includes("fail") || statusText.includes("error") || statusText.includes("undeliver");
    });

  return latestFailure?.errorMessage ?? null;
}

function getDeliveryTickState(message: WhatsappMessage): DeliveryTickState {
  const statusValue = getLatestMessageStatus(message).toLowerCase();

  if (!statusValue) {
    return "pending";
  }

  if (
    statusValue.includes("fail") ||
    statusValue.includes("error") ||
    statusValue.includes("undeliver") ||
    statusValue.includes("rejected")
  ) {
    return "failed";
  }

  if (statusValue.includes("read") || statusValue.includes("seen")) {
    return "read";
  }

  if (statusValue.includes("deliver")) {
    return "delivered";
  }

  if (statusValue.includes("sent") || statusValue.includes("submit") || statusValue.includes("accepted")) {
    return "sent";
  }

  return "pending";
}

function TickIndicator({ state }: { state: DeliveryTickState }) {
  if (state === "pending") {
    return <TickText $state={state}>clock</TickText>;
  }

  if (state === "sent") {
    return <TickText $state={state}>check</TickText>;
  }

  if (state === "failed") {
    return <TickText $state={state}>priority_high</TickText>;
  }

  return <TickText $state={state}>done_all</TickText>;
}

function getConversationTitle(conversation: WhatsappConversation) {
  return (
    conversation.supplierName ||
    conversation.phoneNumber ||
    `Conversation ${conversation.id}`
  );
}

function getMessageMediaId(message: WhatsappMessage) {
  return message.mediaId?.trim() ?? null;
}

function getFileExtension(message: WhatsappMessage) {
  const mimeType = message.mimeType ?? "";

  if (mimeType.includes("/")) {
    const extensionFromMime = mimeType.split("/")[1]?.split(";")[0]?.trim();
    if (extensionFromMime) {
      return extensionFromMime;
    }
  }

  switch ((message.type ?? "").toLowerCase()) {
    case "image":
      return "jpg";
    case "video":
      return "mp4";
    case "audio":
      return "mp3";
    case "document":
      return "pdf";
    default:
      return "bin";
  }
}

function downloadBlob(blob: Blob, fileName: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 1000);
}

export default function AdminCommunications() {
  const dispatch = useAppDispatch();
  const communications = useAppSelector((state) => state.communications);
  const whatsappIntegration = useAppSelector((state) => state.whatsApp);

  useEffect(() => {
    dispatch(setCurrentPage("Communications"));
    dispatch(fetchWhatsappSetting());
    dispatch(fetchConversations());

    return () => {
      dispatch(resetCommunications());
    };
  }, [dispatch]);

  useEffect(() => {
    if (communications.selectedConversationId !== null) {
      dispatch(fetchMessages(communications.selectedConversationId));
    }
  }, [dispatch, communications.selectedConversationId]);

  const selectedConversation = useMemo(
    () =>
      communications.conversations.find(
        (conversation) => conversation.id === communications.selectedConversationId
      ) ?? null,
    [communications.conversations, communications.selectedConversationId]
  );

  const visibleConversations = useMemo(() => {
    const query = communications.searchText.trim().toLowerCase();
    const sorted = [...communications.conversations].sort((left, right) => {
      const leftDate = new Date(left.lastActivity ?? left.created ?? 0).getTime();
      const rightDate = new Date(right.lastActivity ?? right.created ?? 0).getTime();
      return rightDate - leftDate;
    });

    if (!query) {
      return sorted;
    }

    return sorted.filter((conversation) => {
      const haystack = [
        conversation.supplierName,
        conversation.phoneNumber,
        conversation.id?.toString() ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [communications.conversations, communications.searchText]);

  const handleSendReply = async () => {
    const trimmedMessage = communications.replyText.trim();

    if (!communications.selectedConversationId || !trimmedMessage) {
      return;
    }

    try {
      await dispatch(
        sendReply({
          conversationId: communications.selectedConversationId,
          message: trimmedMessage,
        })
      ).unwrap();
      dispatch(showSuccess("Message sent"));
    } catch (error) {
      const message =
        typeof error === "string" ? error : ApplicationError.handleApiError(error, {});
      dispatch(showError(message));
    }
  };

  const handleMediaDownload = async (message: WhatsappMessage) => {
    const mediaId = getMessageMediaId(message);

    if (!mediaId) {
      dispatch(showError("Unable to determine media id for this attachment"));
      return;
    }

    try {
      const mediaBlob = await dispatch(
        downloadConversationMedia({
          mediaId,
          mimeType: message.mimeType,
        })
      ).unwrap();

      const extension = getFileExtension(message);
      const fileName = `whatsapp-media-${message.id ?? mediaId}.${extension}`;
      downloadBlob(mediaBlob, fileName);
    } catch (error) {
      const messageText =
        typeof error === "string" ? error : ApplicationError.handleApiError(error, {});
      dispatch(showError(messageText));
    }
  };

  const openMessageStatusHistory = (message: WhatsappMessage, outgoing: boolean) => {
    dispatch(
      openDrawer({
        id: DrawerID.MessageStatusHistory,
        anchor: "right",
        data: {
          headerText: "Message status",
          message,
          outgoing,
        },
      })
    );
  };

  const activeMessages = communications.selectedConversationId ? communications.messages : [];

  return (
    <PageShell>
      <Hero>
        <HeroCopy>
          <Pill>Communications</Pill>
          <Title>WhatsApp-style inbox for supplier and order conversations</Title>
          <Subtitle>
            Track active supplier conversations, review message history, and reply directly from admin.
          </Subtitle>
        </HeroCopy>

        <HeroStats>
          <StatusCard $tone="dark">
            <StatusLabel>WhatsApp integration</StatusLabel>
            <StatusValue>
              {whatsappIntegration.loading
                ? "Checking..."
                : whatsappIntegration.integrated
                  ? "Connected"
                  : "Not connected"}
            </StatusValue>
          </StatusCard>
          <StatusCard $tone="light">
            <StatusLabel>Conversations</StatusLabel>
            <StatusValue>{communications.conversations.length}</StatusValue>
          </StatusCard>
        </HeroStats>
      </Hero>

      {communications.errorMessage ? (
        <Alert severity="error">{communications.errorMessage}</Alert>
      ) : null}

      <Workspace>
        <Sidebar>
          <PanelHeader>
            <PanelTitle>Inbox</PanelTitle>
            <PanelMeta>
              {communications.loadingConversations ? "Loading conversations" : "Live conversation list"}
            </PanelMeta>
          </PanelHeader>

          <SearchWrap>
            <TextInput
              id="communication-search"
              label="Search conversations"
              value={communications.searchText}
              onChange={(event) => dispatch(setSearchText(event.target.value))}
              iconID={ICON_ID.Search}
            />
          </SearchWrap>

          <ConversationList>
            {communications.loadingConversations && communications.conversations.length === 0 ? (
              <LoadingState>
                <LoadingSpinner />
              </LoadingState>
            ) : null}

            {!communications.loadingConversations && visibleConversations.length === 0 ? (
              <EmptyState>No conversations match the current filter.</EmptyState>
            ) : null}

            {visibleConversations.map((conversation) => {
              const lastMessage = conversation.lastMessage;
              const selected = conversation.id === communications.selectedConversationId;

              return (
                <ConversationCard
                  key={conversation.id}
                  type="button"
                  $selected={selected}
                  onClick={() => dispatch(setSelectedConversationId(conversation.id ?? null))}
                >
                  <ConversationTopRow>
                    <ConversationName>{getConversationTitle(conversation)}</ConversationName>
                    <UnreadBadge>{conversation.unreadCount ?? 0}</UnreadBadge>
                  </ConversationTopRow>

                  <ConversationMeta>{conversation.phoneNumber || "No phone number"}</ConversationMeta>
         
                  <ConversationPreview>
                    {lastMessage?.content || lastMessage?.caption || "No message preview available"}
                  </ConversationPreview>

                  <ConversationFootnote>
                    {conversation.isActive ? "Active" : "Closed"}
                    <span>{formatDateTime(conversation.lastActivity ?? conversation.created)}</span>
                  </ConversationFootnote>
                </ConversationCard>
              );
            })}
          </ConversationList>
        </Sidebar>

        <ThreadPanel>
          {selectedConversation ? (
            <>
              <ThreadHeader>
                <ThreadHeaderCopy>
                  <ThreadTitle>{getConversationTitle(selectedConversation)}</ThreadTitle>
                  <ThreadSubtitle>
                    {selectedConversation.phoneNumber || "Phone number unavailable"} ·{" "}
                    {selectedConversation.isActive ? "Active conversation" : "Inactive conversation"}
                  </ThreadSubtitle>
                </ThreadHeaderCopy>
                <ThreadBadge>{selectedConversation.unreadCount ?? 0} unread</ThreadBadge>
              </ThreadHeader>

              <MessagesPane>
                {communications.loadingMessages ? (
                  <LoadingState>
                    <LoadingSpinner />
                  </LoadingState>
                ) : activeMessages.length === 0 ? (
                  <EmptyState>No messages in this conversation yet.</EmptyState>
                ) : (
                  activeMessages.map((message) => {
                    const outgoing = isOutgoing(message.direction);
                    const deliveryState = getDeliveryTickState(message);
                    const failureError = getMessageFailureError(message);

                    return (
                      <MessageRow key={message.id} $outgoing={outgoing}>
                        <MessageBubble
                          $outgoing={outgoing}
                          onClick={() => openMessageStatusHistory(message, outgoing)}
                        >
                          <MessageHeader>
                            <MessageAuthor>
                              {outgoing ? "Admin" : message.userName || selectedConversation.supplierName || "Supplier"}
                            </MessageAuthor>
                            <MessageMeta>
                              <MessageTime>{formatDateTime(message.timestamp)}</MessageTime>
                            </MessageMeta>
                          </MessageHeader>
                          <MessageBody>
                            {message.content || message.caption || message.errorMessage || "Media message"}
                          </MessageBody>
                          {failureError ? <MessageError>{failureError}</MessageError> : null}
                          {getMessageMediaId(message) ? (
                            <MessageMedia
                              href="#"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                void handleMediaDownload(message);
                              }}
                            >
                              {communications.downloadingMediaId === getMessageMediaId(message)
                                ? "Downloading..."
                                : "Download attachment"}
                            </MessageMedia>
                          ) : null}
                          {message.currentStatus || outgoing ? (
                            <MessageStatus>
                              <MessageStatusLabel>{message.currentStatus ?? "pending"}</MessageStatusLabel>
                              {outgoing ? <TickIndicator state={deliveryState} /> : null}
                            </MessageStatus>
                          ) : null}
                        </MessageBubble>
                      </MessageRow>
                    );
                  })
                )}
              </MessagesPane>

              <Composer>
                <TextInput
                  id="reply-message"
                  label="Write a reply"
                  value={communications.replyText}
                  onChange={(event) => dispatch(setReplyText(event.target.value))}
                  multiline
                  rows={3}
                  sx={{ marginTop: 0 }}
                />
                <ComposerFooter>
                  <ComposerHint>Replies are sent directly through the configured WhatsApp endpoint.</ComposerHint>
                  <Button
                    id="send-whatsapp-reply"
                    label={communications.sendingReply ? "Sending..." : "Send reply"}
                    variant="contained"
                    onClick={handleSendReply}
                    loading={communications.sendingReply}
                    disabled={communications.sendingReply || communications.replyText.trim().length === 0}
                    icon={ICON_ID.Add}
                  />
                </ComposerFooter>
              </Composer>
            </>
          ) : (
            <EmptyThread>
              <EmptyThreadTitle>Select a conversation</EmptyThreadTitle>
              <EmptyThreadText>
                Choose a conversation from the inbox to review the thread and send a reply.
              </EmptyThreadText>
            </EmptyThread>
          )}
        </ThreadPanel>
      </Workspace>
    </PageShell>
  );
}

const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background:
    radial-gradient(circle at top left, rgba(37, 211, 102, 0.18), transparent 34%),
    radial-gradient(circle at top right, rgba(7, 94, 84, 0.16), transparent 28%),
    linear-gradient(180deg, rgba(245, 255, 249, 0.95), rgba(233, 242, 236, 0.96));
  min-height: calc(100vh - 32px);
`;

const Hero = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #075e54 0%, #128c7e 52%, #25d366 100%);
  color: #ffffff;
  box-shadow: 0 24px 60px rgba(7, 94, 84, 0.22);

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const HeroCopy = styled.div`
  flex: 1;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 2.75rem);
  line-height: 1.05;
  max-width: 14ch;
`;

const Subtitle = styled.p`
  max-width: 62ch;
  margin: 12px 0 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 1rem;
`;

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 12px;
  align-items: stretch;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatusCard = styled.div<{ $tone: "dark" | "light" }>`
  padding: 16px;
  border-radius: 20px;
  background: ${({ $tone }) => ($tone === "dark" ? "rgba(0, 0, 0, 0.16)" : "rgba(255, 255, 255, 0.16)")};
  border: 1px solid rgba(255, 255, 255, 0.18);
  min-width: 160px;
  backdrop-filter: blur(8px);
`;

const StatusLabel = styled.div`
  font-size: 0.78rem;
  opacity: 0.82;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const StatusValue = styled.div`
  margin-top: 10px;
  font-size: 1.25rem;
  font-weight: 700;
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(7, 94, 84, 0.12);
  box-shadow: 0 18px 50px rgba(7, 94, 84, 0.08);
  backdrop-filter: blur(10px);
`;

const ThreadPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  min-height: 680px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, rgba(18, 140, 126, 0.08), transparent 22%),
    rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(7, 94, 84, 0.12);
  box-shadow: 0 18px 50px rgba(7, 94, 84, 0.08);
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.application.text};
  opacity: 0.7;
  font-size: 0.9rem;
`;

const SearchWrap = styled.div`
  margin-top: -8px;
`;

const ConversationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ConversationCard = styled.button<ConversationCardProps>`
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $selected }) => ($selected ? "rgba(37, 211, 102, 0.6)" : "rgba(7, 94, 84, 0.12)")};
  background: ${({ $selected }) => ($selected ? "rgba(37, 211, 102, 0.1)" : "#ffffff")};
  box-shadow: ${({ $selected }) => ($selected ? "0 12px 28px rgba(37, 211, 102, 0.12)" : "none")};
  border-radius: 18px;
  padding: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(37, 211, 102, 0.45);
  }
`;

const ConversationTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 10px;
`;

const ConversationName = styled.div`
  font-weight: 700;
  color: #0b3f38;
`;

const UnreadBadge = styled.div`
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #25d366;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
`;

const ConversationMeta = styled.div`
  color: #54656f;
  font-size: 0.9rem;
`;

const ConversationReference = styled.div`
  color: #0b3f38;
  font-size: 0.92rem;
  font-weight: 600;
`;

const ConversationPreview = styled.div`
  color: #667781;
  font-size: 0.92rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ConversationFootnote = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #667781;
  font-size: 0.8rem;
`;

const LoadingState = styled.div`
  padding: 24px 0;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #667781;
  border: 1px dashed rgba(7, 94, 84, 0.18);
  border-radius: 18px;
  background: rgba(37, 211, 102, 0.04);
`;

const ThreadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 4px 4px;
`;

const ThreadHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ThreadTitle = styled.h2`
  margin: 0;
  color: #0b3f38;
`;

const ThreadSubtitle = styled.div`
  color: #667781;
  font-size: 0.92rem;
`;

const ThreadBadge = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(37, 211, 102, 0.12);
  color: #0b3f38;
  font-weight: 700;
`;

const MessagesPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 20px;
  background:
    linear-gradient(rgba(255, 255, 255, 0.76), rgba(255, 255, 255, 0.76)),
    radial-gradient(circle at 20% 20%, rgba(37, 211, 102, 0.08), transparent 18%),
    linear-gradient(180deg, #f7fffb 0%, #eef7f1 100%);
  border: 1px solid rgba(7, 94, 84, 0.08);
  overflow-y: auto;
  min-height: 420px;
`;

const MessageRow = styled.div<{ $outgoing: boolean }>`
  display: flex;
  justify-content: ${({ $outgoing }) => ($outgoing ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div<{ $outgoing: boolean }>`
  max-width: min(78%, 720px);
  padding: 12px 14px;
  border-radius: 18px;
  background: ${({ $outgoing }) => ($outgoing ? "#dcf8c6" : "#ffffff")};
  color: #10211f;
  box-shadow: 0 8px 22px rgba(7, 94, 84, 0.08);
  border: 1px solid ${({ $outgoing }) => ($outgoing ? "rgba(37, 211, 102, 0.22)" : "rgba(7, 94, 84, 0.08)")};
  text-align: left;
  cursor: pointer;

  &:hover {
    box-shadow: 0 10px 24px rgba(7, 94, 84, 0.12);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: #54656f;
`;

const MessageAuthor = styled.div`
  font-weight: 700;
`;

const MessageTime = styled.div`
  white-space: nowrap;
`;

const MessageMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MessageBody = styled.div`
  white-space: pre-wrap;
  line-height: 1.55;
`;

const MessageMedia = styled.a`
  display: inline-flex;
  margin-top: 8px;
  color: #128c7e;
  text-decoration: none;
  font-weight: 600;
`;

const MessageStatus = styled.div`
  margin-top: 8px;
  display: flex;

  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #667781;
`;

const MessageStatusLabel = styled.span`
  text-transform: capitalize;
`;

const MessageError = styled.div`
  margin-top: 8px;
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
  border-radius: 10px;
  padding: 7px 9px;
  font-size: 0.82rem;
`;

const TickText = styled.span<{ $state: DeliveryTickState }>`
  font-family: "Material Symbols Outlined";
  font-size: 1rem;
  line-height: 1;
  color: ${({ $state }) => {
    if ($state === "read") {
      return "#128c7e";
    }

    if ($state === "failed") {
      return "#b42318";
    }

    return "#667781";
  }};
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 0 4px;
`;

const ComposerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ComposerHint = styled.div`
  color: #667781;
  font-size: 0.86rem;
`;

const EmptyThread = styled.div`
  min-height: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  border-radius: 20px;
  border: 1px dashed rgba(7, 94, 84, 0.2);
  background: linear-gradient(180deg, rgba(37, 211, 102, 0.05), rgba(255, 255, 255, 0.9));
`;

const EmptyThreadTitle = styled.h3`
  margin: 0;
  color: #0b3f38;
`;

const EmptyThreadText = styled.p`
  margin: 10px 0 0;
  color: #667781;
  max-width: 40ch;
`;
